import { useEffect, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import LeadChannelsService from '@/services/LeadChannelsService'

export default function LeadChannelsSelect({
	id = 'source',
	name = 'source',
	value = '',
	onChange,
	className = '',
	placeholder = 'Select source',
	disabled = false,
	wrapperClassName = ''
}) {
	const { t } = useTranslation()
	const [options, setOptions] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	useEffect(() => {
		const fetchLeadChannels = async () => {
			setLoading(true)
			setError(null)
			try {
				const result = await LeadChannelsService.getAllLeadChannels()
				if (result.error) {
					throw new Error(result.error)
				}
				setOptions(Array.isArray(result.data) ? result.data : [])
			} catch (err) {
				console.error('Failed to load lead channels:', err)
				setError(i18n.t('leadChannelsSelect.loadFailed'))
				setOptions([])
			} finally {
				setLoading(false)
			}
		}

		fetchLeadChannels()
	}, [])

	// If custom className is provided, use relative positioning (for LeadDetailsSection style)
	// Otherwise use grid layout (for Customer forms style)
	const useRelative = className.includes('rounded-lg') || wrapperClassName.includes('relative')
	const wrapperClass = useRelative 
		? `relative ${wrapperClassName}` 
		: `grid grid-cols-1 ${wrapperClassName}`
	const iconClass = useRelative
		? 'pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400'
		: 'pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-400 sm:size-4'
	const selectClass = useRelative
		? className || 'block w-full appearance-none rounded-lg border-0 py-2.5 pl-3.5 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm bg-white transition-colors'
		: `col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 ${className}`

	return (
		<div className={wrapperClass}>
			<select
				id={id}
				name={name}
				value={value}
				onChange={onChange}
				disabled={disabled || loading}
				className={`${selectClass} ${disabled || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
			>
				<option value="">{loading ? t('leadChannelsSelect.loading') : placeholder}</option>
				{options.map(option => (
					<option key={option._id || option.id} value={option.name}>
						{option.name}
					</option>
				))}
			</select>
			<ChevronDownIcon
				aria-hidden="true"
				className={iconClass}
			/>
			{error && (
				<p className="mt-1 text-sm text-red-600">{error}</p>
			)}
		</div>
	)
}
